import { useEffect, useMemo, useRef } from 'react'
import type { RefObject } from 'react'

import { Command, useCommandState } from 'cmdk'
import { useHotkeys } from 'react-hotkeys-hook'

import { useCommandMenu } from '..'
import { commandMenuFilter } from '~/components/command-menu/commandMenuFilter'
import { CommandMenuFooter } from '~/components/command-menu/components/CommandMenuFooter'
import { CommandContextDecoration } from '~/components/command-menu/components/ContextDecoration'
import { CommandKeyDecoration } from '~/components/command-menu/components/ShortcutDecoration'
import { useCommandArgs } from '~/components/command-menu/hooks/useCommandArgs'
import { useFocusOnMount } from '~/components/command-menu/hooks/useFocusOnMount'
import { COMMAND_PANEL_CLASS } from '~/components/command-menu/styles'
import {
  CommandMenuViewProps,
  CommandProps,
} from '~/components/command-menu/types'

const tokenize = (input: string): string[] =>
  input
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map((token) => token.trim())
    .filter(Boolean)

export const CommandMenuView = ({
  items,
  placeholder,
  view,
  primaryActionLabel,
}: CommandMenuViewProps) => {
  const { contextArgs } = useCommandArgs()
  const { searchValue, setSearchValue, overrideContext, goBack } =
    useCommandMenu()
  const listRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Group items by category while preserving initial order
  const groupedItems = useMemo(() => {
    const grouped = items.reduce(
      (acc, item) => {
        const cat = item.category ?? ''
        if (!acc[cat]) acc[cat] = []
        acc[cat].push(item)
        return acc
      },
      {} as Record<string, CommandProps[]>,
    )

    return Object.entries(grouped).map(([category, items], index) => ({
      category: category || undefined,
      items,
      key: category ? `${category}-${index}` : `no-category-${index}`,
    }))
  }, [items])

  // Determine active context for hiding redundant group headings
  const activeContext = overrideContext
    ? null
    : contextArgs.address
      ? 'account'
      : null

  useFocusOnMount(inputRef, view)

  /**
   * For flow views, backspace navigates to the previous menu when the search is empty.
   * Default view intentionally does nothing on backspace.
   */
  useHotkeys(
    'backspace',
    () => goBack(),
    {
      enableOnFormTags: true,
      enabled: view !== 'default' && searchValue === '',
    },
    [view, searchValue, goBack],
  )

  return (
    <Command
      loop
      label="Command menu"
      filter={commandMenuFilter}
      className={COMMAND_PANEL_CLASS}
    >
      <CommandContextDecoration />
      <Command.Input
        ref={inputRef}
        value={searchValue}
        onValueChange={setSearchValue}
        className="w-full flex-shrink-0 border-b border-gray-100 bg-transparent p-4 py-3 text-base placeholder:text-placeholder focus:outline-none dark:border-gray-800"
        placeholder={placeholder}
      />
      <Command.List
        ref={listRef}
        className="w-full scroll-py-1 overflow-y-auto"
      >
        {groupedItems.map(({ category, items: categoryItems, key }) => {
          const hasVisibleItems = categoryItems.some(
            (item) => !(item.isDisabled?.(contextArgs) ?? false),
          )
          if (!hasVisibleItems) return null

          const isActiveContext = activeContext === category
          return (
            <Command.Group
              key={key}
              value={key}
              // hide the heading if the category is the active context
              heading={isActiveContext ? null : category}
              className="[&_[cmdk-group-heading]]:sticky [&_[cmdk-group-heading]]:top-0 [&_[cmdk-group-heading]]:bg-gray-50 [&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:capitalize [&_[cmdk-group-heading]]:text-secondary [&_[cmdk-group-heading]]:dark:bg-gray-800"
            >
              <div className="w-full p-1">
                {categoryItems.map((item: CommandProps, index: number) => (
                  <CommandItem
                    key={
                      item.id ??
                      (item.category
                        ? `${item.category}-${index}`
                        : `no-category-${index}`)
                    }
                    item={item}
                  />
                ))}
              </div>
            </Command.Group>
          )
        })}
        {items.length === 0 && (
          <div className="flex cursor-default items-center justify-start gap-2.5 rounded-lg px-4 py-2.5 text-sm text-secondary transition-colors">
            No results found
          </div>
        )}
      </Command.List>
      {view !== 'default' && (
        <CommandMenuFooter
          actions={[
            { shortcuts: ['enter'], label: primaryActionLabel ?? 'Select' },
            { shortcuts: ['up', 'down'], label: 'Navigate' },
          ]}
        />
      )}
      <ScrollTopOnFirstSelection listRef={listRef} />
    </Command>
  )
}

const CommandItem = ({ item }: { item: CommandProps }) => {
  const { contextArgs, actionArgs } = useCommandArgs()

  const label = item.label(contextArgs)
  const icon = item.icon(contextArgs)
  const shortcut = item.shortcuts?.[0]
  const providedValue = item.value?.(contextArgs)

  // Determine the `value` cmdk uses for matching and tracking selection
  const itemValue =
    (typeof providedValue === 'string' && providedValue.trim()) ||
    (typeof label === 'string' && label.trim()) ||
    item.id ||
    item.category ||
    ''

  // Build deduplicated search tokens for fuzzy matching
  const itemKeywords = useMemo(() => {
    const texts = [
      itemValue,
      typeof label === 'string' ? label : null,
      typeof providedValue === 'string' ? providedValue : null,
      item.category,
      shortcut,
    ].filter((t): t is string => typeof t === 'string' && t.length > 0)

    return [...new Set(texts.flatMap(tokenize))]
  }, [itemValue, label, providedValue, item.category, shortcut])

  const isDisabled = item.isDisabled?.(contextArgs) ?? false

  if (isDisabled) return null

  return (
    <Command.Item
      value={itemValue}
      keywords={itemKeywords.length > 0 ? itemKeywords : undefined}
      onSelect={() => item.onSelect({ ...contextArgs, actions: actionArgs })}
      className="group flex cursor-pointer items-center justify-start gap-2.5 rounded-lg px-3 py-2 pr-2 text-sm transition-colors hover:bg-gray-100 data-[selected=true]:bg-gray-100 dark:hover:bg-gray-800 dark:data-[selected=true]:bg-gray-800"
    >
      {icon && (
        <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center [&>svg]:size-4 [&>svg]:stroke-[1.5]">
          {icon}
        </div>
      )}
      <span className="min-w-0 truncate">{label}</span>
      <div className="flex-1" />
      {shortcut && <CommandKeyDecoration shortcut={shortcut} />}
    </Command.Item>
  )
}

/**
 * Resets the list scroll position when the selection wraps back to the first item.
 * cmdk keeps keyboard focus but can leave the list scrolled midway; this snaps the
 * viewport to the top so the first command stays visible when cycling upwards.
 */
const ScrollTopOnFirstSelection = ({
  listRef,
}: {
  listRef: RefObject<HTMLDivElement | null>
}) => {
  const selectedItemId = useCommandState((state) => state.selectedItemId)

  useEffect(() => {
    const listEl = listRef.current
    if (!listEl) return

    const selectedEl = listEl.querySelector<HTMLElement>(
      '[cmdk-item][aria-selected="true"]',
    )
    if (!selectedEl) return

    const firstSelectableItem = listEl.querySelector<HTMLElement>(
      '[cmdk-item]:not([aria-disabled="true"])',
    )
    if (!firstSelectableItem) return

    if (selectedEl === firstSelectableItem && listEl.scrollTop !== 0) {
      listEl.scrollTop = 0
    }
  }, [selectedItemId, listRef])

  return null
}
